import {
  type InternalPayPointDto,
  PayPointType,
  Permission,
  type ProductCode,
} from "Infrastructure/Api/Api";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useCodeListQuery } from "Api/Queries/CodeList/useCodeListQuery";
import { useMerchantEmployees } from "Api/Queries/Merchants/Employees/useMerchantEmployees";
import { useMerchantFacilities } from "Api/Queries/Merchants/useMerchantFacilities";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import type { AdvancedFieldsVariant } from "Components/Paypoints/PaypointAccessSection";
import { PaypointForm } from "Components/Paypoints/PaypointForm";
import { pickMainBankAccount } from "Utils/AccountUtils";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { fromPayPointSettingsDto } from "Components/Paypoints/paymentType";
import type { PaypointFormData } from "Components/Paypoints/schema";
import { useCheckPermission } from "Hooks/usePermission";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { REGION_TO_MARKET_CURRENCY } from "Utils/CurrencyUtils";

type EditPaypointProps = {
  paypoint: InternalPayPointDto;
  onSuccess: (paypointPublicID: string) => void;
  productID?: number | null;
  productCode?: ProductCode | null;
  nfcCardNo?: string;
  merchantPublicID?: string;
  isTerminalProduct?: boolean;
};

type CreatePaypointProps = {
  paypoint?: InternalPayPointDto;
  onSuccess: (paypointPublicID: string) => void;
  productID: number | null;
  productCode?: ProductCode | null;
  nfcCardNo?: string;
  merchantPublicID?: string;
  isTerminalProduct?: boolean;
};

const resolveAdvancedFieldVariant = (
  isMerchant: boolean,
  canRead: boolean,
): AdvancedFieldsVariant => {
  if (isMerchant) {
    return "editable";
  }
  return canRead ? "readOnly" : "notAvailable";
};

type Props = EditPaypointProps | CreatePaypointProps;

export const PaypointFormInitializer = ({
  paypoint,
  onSuccess,
  productID,
  productCode,
  nfcCardNo,
  merchantPublicID,
  isTerminalProduct = false,
}: Props) => {
  const isMerchant = useIsCurrentUserMerchant();
  const isHunter = useIsCurrentUserHunter();
  const currentUser = useLoggedInUser();
  const hasBankAccountReadPermission = useCheckPermission(
    AccessRightCodes.Banking,
    Permission.Read,
  );
  const hasEmployeesReadPermission = useCheckPermission(
    AccessRightCodes.Employees,
    Permission.Read,
  );
  const canReadBankAccounts = !isHunter && hasBankAccountReadPermission;
  const canReadEmployees = !isHunter && hasEmployeesReadPermission;

  const { data: merchantDetail, isLoading: isLoadingMerchant } =
    useGetMerchantQuery(merchantPublicID, !isMerchant && !!merchantPublicID);
  const merchantUserID = merchantDetail?.merchantUserID ?? undefined;

  const { data: accounts, isLoading: isLoadingAccounts } = useBankingAccounts(
    !isMerchant ? merchantUserID : undefined,
    canReadBankAccounts,
  );
  const { data: employees, isLoading: isLoadingEmployees } =
    useMerchantEmployees(merchantPublicID, { limit: 100 }, canReadEmployees);
  const { data: facilities, isLoading: isLoadingFacilities } =
    useMerchantFacilities(merchantPublicID);
  // The currency dropdown derives its options from the codelist (useCurrencyList) — nothing else
  // guarantees it is fetched before the form mounts, so gate on it like the other sources.
  const { isLoading: isLoadingCodeList } = useCodeListQuery();

  const isLoading =
    isLoadingMerchant ||
    isLoadingFacilities ||
    isLoadingEmployees ||
    isLoadingAccounts ||
    isLoadingCodeList;

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  const defaultCurrencyCode = currentUser?.region
    ? REGION_TO_MARKET_CURRENCY[currentUser.region]
    : undefined;

  // The paypoint form defaults to the market currency — its default account must sit
  // on the same rail.
  const defaultAccountPublicID = canReadBankAccounts
    ? pickMainBankAccount(accounts?.items || [], defaultCurrencyCode)?.publicID
    : undefined;

  const defaultValues: Partial<PaypointFormData> = paypoint
    ? {
        ...paypoint,
        name: paypoint.name ?? undefined,
        ...fromPayPointSettingsDto(paypoint.settings, defaultCurrencyCode),
      }
    : {
        type: PayPointType.NFC,
        ...fromPayPointSettingsDto(null, defaultCurrencyCode),
        nfcCardNo,
        employeePublicIDs: [],
        bankAccountPublicID: defaultAccountPublicID,
      };

  return (
    <PaypointForm
      facilities={facilities}
      defaultValues={defaultValues}
      accounts={accounts?.items || []}
      employees={employees?.items || []}
      onSuccess={onSuccess}
      merchantPublicID={merchantPublicID}
      productID={productID}
      productCode={productCode ?? paypoint?.productCode}
      bankAccountVariant={resolveAdvancedFieldVariant(
        isMerchant,
        canReadBankAccounts,
      )}
      employeesVariant={resolveAdvancedFieldVariant(
        isMerchant,
        canReadEmployees,
      )}
      isBalanceVisible={isMerchant}
      isTerminalProduct={isTerminalProduct}
    />
  );
};
